/**
 * Catálogo do domínio `rdo` (T52, CAT-01).
 *
 * Origem: `src/shared/autorizacao/rdo.ts`, mais a única regra de `avisos.ts`.
 *
 * É o primeiro domínio com **ABAC real declarado**: `rdo.escrita` não concede em
 * todo RDO, só naquele em que a pessoa é o site manager ou o suplente. Isso hoje
 * são duas chamadas `can(...)` quase idênticas em `rdo.ts:16-24`; aqui é uma
 * concessão com duas relações, e o compilador (T51) emite uma regra por relação.
 */
import type { Concessao, NomePapel, Papel } from "../catalogo/vocabulario";

/**
 * `rdo_by_obra` fica FORA do catálogo, e é decisão, não esquecimento.
 *
 * `rdo.ts:11` concede `read` em RDO condicionado a `{ obra: { $in: user.obra } }`
 * — condição que depende do USUÁRIO, não do registro, exatamente como a ACL de
 * `Diretorio`. O destino dela não é uma concessão: é o `aplicarEscopo` do
 * `ADR-0004`, que injeta a lista de obras em toda regra de um usuário `cliente`.
 *
 * O `LEGADO` já marca `rdo_by_obra` como `null` e diz que ela morre quando
 * `PAPEIS_RDO` conceder `rdo.leitura`. **Isso é uma ampliação enquanto
 * `aplicarEscopo` não existir** (T59/T60): quem hoje lê só os RDOs das suas obras
 * passa a ler todos. A decisão do time em `PADRONIZACAO-ROLES.md §8.1` é
 * justamente essa — "Gali lê tudo", e o escopo existe só para `tipo: "cliente"`.
 * Registrado aqui para a T54 não ligar o catálogo antes de saber disso.
 */
export const CONCESSOES_BASE_RDO: readonly Concessao[] = [];

export const PAPEIS_RDO = {
  "rdo.admin": {
    dominio: "rdo",
    nivel: "admin",
    concessoes: [{ acoes: ["manage"], recurso: "RDO" }],
  },
  /**
   * O papel de site manager de diário, agora com a relação declarada em vez de
   * escrita à mão. `exigeRelacao` com duas entradas vira duas regras, avaliadas em
   * OR pelo CASL — ver `compilador.ts`.
   *
   * **O `read` saiu daqui na T56 (AD-031 D6).** Ele concedia leitura só do diário
   * em que a pessoa é o site manager, e a decisão do usuário foi textual: *"Site
   * manager lê rdo de todas as obras mas cria apenas na sua"*. Leitura ampla é
   * `rdo.leitura`, incondicional, e `GRUPOS.SITE_MANAGER` passou a carregar os
   * dois papéis. Deixar o `read` nos dois lugares faria a leitura restrita parecer
   * política viva quando ela não é mais.
   *
   * **`delete` entrou na conferência de acessos (RDO-03, 2026-10-01):** o site
   * manager (ou o suplente) exclui o diário da própria obra, sob a mesma relação
   * de `create` e `update`. Quem não é nenhum dos dois não exclui.
   */
  "rdo.escrita": {
    dominio: "rdo",
    nivel: "escrita",
    concessoes: [
      {
        acoes: ["create", "update", "delete"],
        recurso: "RDO",
        exigeRelacao: ["site_manager", "suplente"],
      },
    ],
  },
  /** Carregado por `GRUPOS.PLANEJAMENTO`, e é o que substitui `rdo_by_obra` (ver acima). */
  "rdo.leitura": {
    dominio: "rdo",
    nivel: "leitura",
    concessoes: [{ acoes: ["read"], recurso: "RDO" }],
  },
  /**
   * Etapa de workflow de planejamento. O `aviso-atraso` vem de `avisos.ts`, que é
   * um arquivo só para essa regra e some quando a Fase 7 fechar.
   */
  "rdo.config": {
    dominio: "rdo",
    nivel: "config",
    concessoes: [{ acoes: ["config", "data_planejada", "aviso-atraso"], recurso: "RDO" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;
